import React, { useMemo, useState } from "react";
import { Tooltip } from "@mui/material";
import { FiLock } from "react-icons/fi";
import {
  RiPrinterLine,
  RiLineChartLine,
  RiMapPin2Line,
} from "react-icons/ri";
import { dashboardTooltipProps } from "../../modules/Dashboard/styles";
import { FormModal } from "../FormModal";
import {
  LockedOverlay,
  DiscreetLockCircle,
  DiscreetLockInner,
  UnlockBenefitsList,
  UnlockBenefitItem,
  UnlockBenefitIcon,
  UnlockBenefitContent,
  UnlockBenefitTitle,
  UnlockBenefitDescription,
} from "./styles";

const LOCK_ANIMATION_CYCLE_MS = 6000;

export interface ILockedFeatureIconProps {
  title?: string;
  withOverlay?: boolean;
  size?: number;
  iconSize?: number;
  style?: React.CSSProperties;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function LockedFeatureIcon({
  title = "Desbloqueie tornando-se um Licenciado",
  withOverlay = false,
  size = 38,
  iconSize = 17,
  style,
  open,
  onOpenChange,
}: ILockedFeatureIconProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isModalOpen = open !== undefined ? open : internalOpen;

  const syncDelayMs = useMemo(() => {
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    return -Math.round(now % LOCK_ANIMATION_CYCLE_MS);
  }, []);

  const setModalOpen = (nextOpen: boolean) => {
    if (open === undefined) {
      setInternalOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  const handleOpenModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleKnowMore = () => {
    window.open(
      "https://wa.me/5511981669461?text=Ol%C3%A1!%20Gostaria%20de%20saber%20mais%20sobre%20como%20me%20tornar%20um%20Licenciado%20Fix%20it%20para%20desbloquear%20os%20recursos%20exclusivos.",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const lockButton = (
    <Tooltip
      title={title}
      arrow
      placement="top"
      disableTouchListener
      componentsProps={dashboardTooltipProps}
    >
      <DiscreetLockCircle
        $size={size}
        $iconSize={iconSize}
        style={style}
        onClick={handleOpenModal}
      >
        <DiscreetLockInner $syncDelayMs={syncDelayMs}>
          <FiLock />
        </DiscreetLockInner>
      </DiscreetLockCircle>
    </Tooltip>
  );

  const unlockModal = isModalOpen ? (
    <div onClick={(e) => e.stopPropagation()} style={{ display: "contents" }}>
      <FormModal
        open={isModalOpen}
        onRequestClose={handleCloseModal}
        title={title}
        subtitle="Este recurso é exclusivo para Licenciados Fix it. Ao fazer o upgrade, você desbloqueia mais vantagens:"
        cancelText="Cancelar"
        saveText="Quero saber mais"
        onSave={handleKnowMore}
        width={620}
      >
        <UnlockBenefitsList>
          <UnlockBenefitItem>
            <UnlockBenefitIcon>
              <RiPrinterLine />
            </UnlockBenefitIcon>
            <UnlockBenefitContent>
              <UnlockBenefitTitle>
                Módulo Completo de Impressão 3D
              </UnlockBenefitTitle>
              <UnlockBenefitDescription>
                Imprima todos os modelos de Órteses Fix it 3D (podendo ser
                coloridas na sua impressora), além de ter acesso a outros
                dispositivos e brindes gratuitos para produzir na clínica.
              </UnlockBenefitDescription>
            </UnlockBenefitContent>
          </UnlockBenefitItem>

          <UnlockBenefitItem>
            <UnlockBenefitIcon>
              <RiLineChartLine />
            </UnlockBenefitIcon>
            <UnlockBenefitContent>
              <UnlockBenefitTitle>Gestão Completa</UnlockBenefitTitle>
              <UnlockBenefitDescription>
                Painel completo para gerenciamento de pacientes, acompanhamento
                de pedidos, controle de múltiplos usuários e métricas de
                desempenho.
              </UnlockBenefitDescription>
            </UnlockBenefitContent>
          </UnlockBenefitItem>

          <UnlockBenefitItem>
            <UnlockBenefitIcon>
              <RiMapPin2Line />
            </UnlockBenefitIcon>
            <UnlockBenefitContent>
              <UnlockBenefitTitle>Destaque no Localizador</UnlockBenefitTitle>
              <UnlockBenefitDescription>
                Conquiste a Estrela Dourada e apareça com prioridade no topo das
                buscas para os pacientes.
              </UnlockBenefitDescription>
            </UnlockBenefitContent>
          </UnlockBenefitItem>
        </UnlockBenefitsList>
      </FormModal>
    </div>
  ) : null;

  if (withOverlay) {
    return (
      <>
        <LockedOverlay
          role="button"
          tabIndex={0}
          onClick={handleOpenModal}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setModalOpen(true);
            }
          }}
        >
          {lockButton}
        </LockedOverlay>
        {unlockModal}
      </>
    );
  }

  return (
    <>
      {lockButton}
      {unlockModal}
    </>
  );
}

export { LockedOverlay, DiscreetLockCircle };
